import React, { useState, useEffect, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Link, Button, withStyles } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { func, object, arrayOf, bool, number } from 'prop-types';
import { push } from 'connected-react-router';

import ListContainer from '../../list';
import { AlertDialog } from '../../../components';
import {
  getCustomFieldsList,
  getIsLoading,
  getCustomFieldsListTotalElements,
  getIsDeleteSuccess,
  getIsError,
  getErrors,
} from '../../../reducers/customFields';
import { getUserDetails, getUserPrefs } from '../../../reducers/portalConfig';
import { hasPublisherRole } from '../../../utils';
import { getI18n } from '../../../utils/intl';
import usePrefs from '../../../hooks/userPrefs';
import { fetchCustomFieldsLegacy, deleteCustomFields } from '../../../actions/customFields';
import {
  calculateTotalPages,
  getRowsForPage,
  STATE_ENABLED,
} from './utils';
import { getStatusColor, getStatusLabel } from "../../Api/List/utils";
import { ALERT_SUCCESS } from '../../../constants';
import styles from './styles';

const GRID_ROWS_PER_PAGE_OPTIONS = [24, 48, 72, 96];
const GRID_ROWS_PER_PAGE_DEFAULT_OPTION = 24;

export const CustomFieldsListPage = (props) => {
  const {
    customFields,
    classes,
    fetchCustomFieldsLegacy,
    isLoading,
    totalElements,
    user,
    userPrefs,
    isDeleteSuccess,
    deleteCustomFields,
    pageErrors,
    isError,
    userContext,
  } = props;
  const intl = getI18n(useIntl());
  const [getUserPref, setUserPref] = usePrefs(userPrefs);

  useEffect(() => {
    if (!hasPublisherRole(userContext)) { props.push('/404'); }
  }, [userContext]);

  const [rowsPerPage, setRowsPerPage] = useState(
    GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  );
  const [totalPages, setTotalPages] = useState(0);
  const [customFieldsForPage, setCustomFieldsForPage] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [errors, setErrors] = useState([]);

  const [page, setPage] = useState(0);
  const onChangePreviousPage = () => {
    onChangePage(page - 1);
  };
  const onChangeNextPage = () => {
    onChangePage(page + 1);
  };

  const [prefsRead, setPrefsRead] = useState(false);

  const { username, tenantId } = user;
  useEffect(() => {
    if (username && tenantId) {
      if (getUserPref(username, tenantId, 'PAGE_SIZE')) {
        setRowsPerPage(getUserPref(username, tenantId, 'PAGE_SIZE'));
      }
      setPrefsRead(true);
    }
  }, [username, tenantId]);

  useEffect(() => {
    fetchCustomFieldsLegacy();
  }, []);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    setErrors(isError ? pageErrors : []);
  }, [isError, pageErrors]);

  useEffect(() => {
    if (isDeleteSuccess) {
      notifyMessages(
        ALERT_SUCCESS,
        intl.getI18nMessage('label.custom.field.deleted'),
      );
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    }
  }, [isDeleteSuccess]);

  useEffect(() => {
    fetchCustomFieldsLegacy();
  }, [isDeleteSuccess]);

  // Pagination rows logic
  useEffect(() => {
    if (prefsRead && customFields) {
      setTotalPages(calculateTotalPages(customFields, rowsPerPage));
      setCustomFieldsForPage(getRowsForPage(customFields, page, rowsPerPage));
    }
  }, [page, prefsRead, rowsPerPage, customFields]);
  const onChangePage = (newPage) => {
    setErrors([]);
    setPage(newPage);
  };
  const onChangeRowsPerPage = (newRowsPerPage) => {
    setErrors([]);
    setRowsPerPage(newRowsPerPage);
    setUserPref(username, tenantId, 'PAGE_SIZE', newRowsPerPage);
    setPage(0);
  };

  const onDelete = (item) => {
    setErrors([]);
    setShowConfirmDialog(true);
    setSelectedRow(item);
  }

  const deleteConfirmDialog = () => {
    setShowConfirmDialog(false);
    deleteCustomFields(selectedRow);
  }

  const getCustomFieldsListColumns = () => [
    {
      id: 'Name',
      secondaryId: 'Uuid',
      label: intl.getI18nFormattedMessage('label.custom.field'),
      format: (value, secondaryValue) => {
        return(
          <Link underline="none" href={`/admin/settings/custom-fields#edit/${secondaryValue}`}>
            {value}
          </Link>
        )
      },
    },
    {
      id: 'EntityType',
      label: intl.getI18nFormattedMessage('label.custom.fields.list.column.form'),
      value: (item) => {
        switch (item.EntityType) {
          case 'API':
            return intl.getI18nFormattedMessage(
              'label.custom.fields.list.column.form.api',
            );
          case 'APPLICATION':
            return intl.getI18nFormattedMessage(
              'label.custom.fields.list.column.form.application',
            );
          default:
            return item.EntityType;
        }
      },
    },
    {
      id: 'Type',
      label: intl.getI18nFormattedMessage('label.custom.fields.list.column.type'),
      value: (item) => {
        switch (item.Type) {
          case 'TEXT':
            return intl.getI18nFormattedMessage(
              'label.custom.fields.list.column.type.text',
            );
          case 'SINGLE_SELECT':
            return intl.getI18nFormattedMessage(
              'label.custom.fields.list.column.type.single.select',
            );
          default:
            return item.Type;
        }
      },
    },
    {
      id: 'Required',
      label: intl.getI18nFormattedMessage(
        'label.required.field',
      ),
      value: (item) => (item.Required ? 'Yes' : 'No'),
    },
    {
      id: 'Status',
      label: intl.getI18nFormattedMessage(
        'label.status',
      ),
      value: (item) => {
        const statusIconStyle = {
          fill: getStatusColor(item.Status),
        };
        return (
          <span
            className={classes.itemState}
          >
            <FiberManualRecordIcon size="0.82em" style={statusIconStyle} className={classes.statusIcon} />
            {getStatusLabel(item.Status)}
          </span>);
      },
    },
    {
      id: 'Uuid',
      label: intl.getI18nFormattedMessage('label.actions'),
      value: (item) => {
          return(
            <div className={classes.buttonContainer}>
              <Button
                id={`${'status'-item.Uuid}`}
                variant="text"
                disabled={item.Status === STATE_ENABLED}
                onClick={() => onDelete(item)}
                className={classes.deleteText}
              >
                {intl.getI18nFormattedMessage('label.delete.button')}
              </Button>
            </div>
          )
      },
    },
  ];

  return (
    <Fragment>
      {showConfirmDialog && (
        <AlertDialog
          isOpen={showConfirmDialog}
          title={intl.getI18nMessage('warning.status')}
          description={intl.getI18nMessage('label.custom.field.delete.confirmation.text')}
          submitText={intl.getI18nMessage('label.delete.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          onClose={() => setShowConfirmDialog(false)}
          onSubmit={() => deleteConfirmDialog()}
          onCancel={() => setShowConfirmDialog(false)}
          dialogId="custom-fields-list-delete-dialog"
          submitButtonClass={classes.submitButton}
        />
      )}
      <ListContainer
        listPageId="custom-fields-list-page"
        isLoading={isLoading}
        notificationId="custom-fields-list-notifications"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationStatus}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        pageHeaderTitle={intl.getI18nMessage(
          'label.custom.fields.list.page.title',
        )}
        addButtonLabel={intl.getI18nMessage('label.custom.fields.add')}
        onAdd={() => window.location.href='/admin/settings/custom-fields#add'}
        columns={getCustomFieldsListColumns(intl, classes) || []}
        rows={customFieldsForPage}
        noResultsMessage={intl.getI18nMessage('label.custom.fields.no.results')}
        page={page}
        rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
        totalElements={totalElements}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
        errors={errors}
      />
    </Fragment>
  );
};

CustomFieldsListPage.propTypes = {
  customFields: arrayOf(object),
  classes: object,
  fetchCustomFieldsLegacy: func,
  push: func,
  isLoading: bool,
  totalElements: number,
  user: object,
  userPrefs: object,
  isDeleteSuccess: bool,
  deleteCustomFields: func,
  pageErrors: arrayOf(object),
  isError: bool,
  userContext: object,
};

const mapStateToProps = (state) => ({
  customFields: getCustomFieldsList(state),
  isLoading: getIsLoading(state),
  totalElements: getCustomFieldsListTotalElements(state),
  user: getUserDetails(state),
  userPrefs: getUserPrefs(state),
  isDeleteSuccess: getIsDeleteSuccess(state),
  isError: getIsError(state),
  pageErrors: getErrors(state),
  userContext: getUserDetails(state),
});

const mapDispatchToProps = {
  push,
  fetchCustomFieldsLegacy,
  deleteCustomFields,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(CustomFieldsListPage);
